import React from 'react';
import { IHTCalculation, EstateAssets, EstateProfile } from '../../types';
import { assessIHTRiskFlags, IHTRiskFlag, IHTRiskSeverity } from '../../server/services/ihtRiskFlags';
import { ShieldAlert, ArrowRight } from 'lucide-react';

interface Props {
 ihtCalc?: IHTCalculation | null;
 assets?: EstateAssets;
 profile?: EstateProfile;
}

const MAX_FLAGS = 5;

const SEVERITY_STYLE: Record<IHTRiskSeverity, {
 badge: string; border: string; bg: string; icon: string;
}> = {
 High: {
 badge: 'bg-white text-[#1A1A1A] border-[#1A1A1A]',
 border: 'border-[#E8E4DB]',
 bg: 'bg-white',
 icon: 'text-[#1A1A1A]',
 },
 Medium: {
 badge: 'bg-[#FAF3E0] text-[#7A5A00] border-[#E8D89A]',
 border: 'border-[#E8D89A]',
 bg: 'bg-[#FAF3E0]',
 icon: 'text-[#7A5A00]',
 },
 Low: {
 badge: 'bg-[#EEF0FA] text-[#3730A3] border-[#C7CBEA]',
 border: 'border-[#C7CBEA]',
 bg: 'bg-[#EEF0FA]',
 icon: 'text-[#3730A3]',
 },
};

export const IHTRiskFlagsPanel: React.FC<Props> = ({ ihtCalc, assets, profile }) => {
 const allFlags = assessIHTRiskFlags(ihtCalc ?? null, assets, profile);
 if (allFlags.length === 0) return null;

 const flags = allFlags.slice(0, MAX_FLAGS);
 const hidden = allFlags.length - flags.length;

 const highCount = allFlags.filter(f => f.severity === 'High').length;

 return (
 <div className="bg-white border border-[#E8E4DB] rounded overflow-hidden">
 {/* Header */}
 <div className="bg-[#1A1A1A] px-6 py-4">
 <div className="flex items-center gap-2">
 <ShieldAlert className="w-4 h-4 text-[#7A5A00]" />
 <h2 className="text-xl font-bold text-white">IHT Risk Flags</h2>
 </div>
 <p className="text-[#AFA99F] text-sm mt-1">
 {allFlags.length} issue{allFlags.length !== 1 ? 's' : ''} identified
 {highCount > 0 && ` — ${highCount} high priority`}.
 Review before relying on the tax result.
 </p>
 </div>

 <div className="p-6 space-y-3">
 {flags.map((flag) => {
 const style = SEVERITY_STYLE[flag.severity];
 return (
 <div
 key={flag.id}
 className={`rounded-lg border ${style.border} ${style.bg} p-4`}
 >
 <div className="flex items-start gap-3">
 <ShieldAlert className={`w-4 h-4 mt-0.5 shrink-0 ${style.icon}`} />
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 flex-wrap">
 <span className="text-sm font-bold text-[#1A1A1A]">{flag.title}</span>
 <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full border ${style.badge}`}>
 {flag.severity}
 </span>
 </div>
 <p className="text-xs text-[#3F3D39] mt-1.5 leading-relaxed">
 {flag.why}
 </p>
 <div className="flex items-start gap-1.5 mt-2">
 <ArrowRight className="w-3 h-3 mt-0.5 text-[#AFA99F] shrink-0" />
 <p className="text-xs font-medium text-[#3F3D39]">
 {flag.nextStep}
 </p>
 </div>
 </div>
 </div>
 </div>
 );
 })}

 {hidden > 0 && (
 <p className="text-xs text-[#AFA99F] text-center pt-1">
 +{hidden} more lower-priority flag{hidden !== 1 ? 's' : ''} not shown.
 </p>
 )}
 </div>
 </div>
 );
};
